import React, { useState } from 'react';
import './AddWashroomForm.css';
import { FaTimes, FaUpload, FaTrash } from 'react-icons/fa';
import config from '../config';

const AddWashroomForm = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    images: [],
    googleMapsLink: '',
    latitude: null,
    longitude: null
  });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Update form data with new images
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...files]
      }));
      
      // Create preview URLs for new images
      const newPreviews = files.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(file);
        });
      });

      Promise.all(newPreviews).then(previews => {
        setImagePreviews(prev => [...prev, ...previews]);
      });
    }
  };

  // Handle image removal
  const handleImageRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Extract coordinates from Google Maps link
  const extractCoordinates = async (url) => {
    setIsLoading(true);
    setError('');
    
    try {
      // First, resolve any shortened URL
      let resolvedUrl = url;
      if (url.includes('goo.gl/maps') || url.includes('maps.app.goo.gl')) {
        try {
          const response = await fetch(url, { 
            method: 'HEAD',
            redirect: 'follow',
            headers: {
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
            }
          });
          resolvedUrl = response.url;
        } catch (err) {
          console.error('Error resolving shortened URL:', err);
          setError('Could not resolve the shortened URL. Please try using the full Google Maps URL.');
          return;
        }
      }

      // Try to extract coordinates from different Google Maps URL formats
      let lat = null;
      let lng = null;
      
      // Format: https://www.google.com/maps?q=XX.XXXXX,YY.YYYYY
      const qParam = resolvedUrl.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
      
      // Format: https://www.google.com/maps/@XX.XXXXX,YY.YYYYY,15z
      const atParam = resolvedUrl.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      
      // Format: https://www.google.com/maps/place/.../@XX.XXXXX,YY.YYYYY,15z
      const placeParam = resolvedUrl.match(/@(-?\d+\.\d+),(-?\d+\.\d+),/);
      
      if (qParam && qParam.length === 3) {
        lat = parseFloat(qParam[1]);
        lng = parseFloat(qParam[2]);
      } else if (atParam && atParam.length === 3) {
        lat = parseFloat(atParam[1]);
        lng = parseFloat(atParam[2]);
      } else if (placeParam && placeParam.length === 3) {
        lat = parseFloat(placeParam[1]);
        lng = parseFloat(placeParam[2]);
      }
      
      if (lat && lng) {
        setFormData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng
        }));
        setError('');
      } else {
        setError('Could not extract coordinates from the provided link. Please ensure it\'s a valid Google Maps URL.');
      }
    } catch (err) {
      setError('Error processing the Google Maps link. Please try again.');
      console.error('Error extracting coordinates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.description || formData.images.length === 0 || !formData.latitude || !formData.longitude) {
      setError('Please fill in all fields, upload at least one image, and ensure coordinates are extracted from the Google Maps link.');
      return;
    }
    
    setIsLoading(true);
    setError('');

    console.log('Submitting form data:', formData);

    try {
      // Compress and convert images to base64
      const imagePromises = formData.images.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              // Create canvas for compression
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              // Calculate new dimensions (max 400px on the longest side)
              let width = img.width;
              let height = img.height;
              const maxSize = 400;
              
              if (width > height && width > maxSize) {
                height = (height * maxSize) / width;
                width = maxSize;
              } else if (height > maxSize) {
                width = (width * maxSize) / height;
                height = maxSize;
              }
              
              canvas.width = width;
              canvas.height = height;
              
              // Draw and compress image with higher compression
              ctx.drawImage(img, 0, 0, width, height);
              const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.5);
              
              // Log the size of the compressed image
              const base64Length = compressedDataUrl.length;
              const sizeInMB = (base64Length * 3/4) / (1024 * 1024);
              console.log(`Compressed image size: ${sizeInMB.toFixed(2)}MB`);
              
              resolve({
                name: file.name,
                size: file.size,
                type: 'image/jpeg',
                data: compressedDataUrl
              });
            };
            img.onerror = reject;
            img.src = e.target.result;
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const imagesWithData = await Promise.all(imagePromises);

      // Log total payload size
      const totalPayloadSize = JSON.stringify({
        ...formData,
        images: imagesWithData
      }).length;
      const totalSizeInMB = (totalPayloadSize * 3/4) / (1024 * 1024);
      console.log(`Total payload size: ${totalSizeInMB.toFixed(2)}MB`);

      // Send form data to backend for email submission
      const response = await fetch(`${config.apiUrl}/submit-washroom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          images: imagesWithData
        }),
      });

      console.log('Response status:', response.status);
      const responseData = await response.json();
      console.log('Response data:', responseData);

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to submit washroom details');
      }

      // Show success message
      alert('Thank you for your submission! The washroom details have been sent for review. We will add it to the system if appropriate.');
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        images: [],
        googleMapsLink: '',
        latitude: null,
        longitude: null
      });
      setImagePreviews([]);
      
      // Close the popup
      onClose();
    } catch (err) {
      console.error('Error submitting washroom:', err);
      setError(err.message || 'Failed to submit washroom details. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-container">
        <div className="popup-header">
          <h2>Add New Washroom</h2>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="washroom-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="name">Washroom Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter washroom name"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Washroom Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the washroom facilities, accessibility, etc."
              rows="4"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="images">Upload Images</label>
            <div className="image-upload-container">
              <input
                type="file"
                id="images"
                name="images"
                accept="image/*"
                onChange={handleImageUpload}
                className="image-input"
                multiple
                required
              />
              <label htmlFor="images" className="upload-button">
                <FaUpload /> Choose Images
              </label>
              <div className="image-previews-container">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="image-preview-wrapper">
                    <div className="image-preview">
                      <img src={preview} alt={`Preview ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-image-button"
                        onClick={() => handleImageRemove(index)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="googleMapsLink">Google Maps Link</label>
            <div className="maps-link-container">
              <input
                type="url"
                id="googleMapsLink"
                name="googleMapsLink"
                value={formData.googleMapsLink}
                onChange={handleChange}
                placeholder="Paste Google Maps link here"
                required
              />
              <button 
                type="button" 
                className="extract-button"
                onClick={() => extractCoordinates(formData.googleMapsLink)}
                disabled={isLoading || !formData.googleMapsLink}
              >
                Extract Coordinates
              </button>
            </div>
          </div>
          
          {(formData.latitude && formData.longitude) && (
            <div className="coordinates-display">
              <p>Latitude: {formData.latitude}</p>
              <p>Longitude: {formData.longitude}</p>
            </div>
          )}
          
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-button">
              Add Washroom
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddWashroomForm; 